import React, {useMemo, useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {ChatAltIcon, InboxIcon, PencilIcon, ThumbDownIcon, ThumbUpIcon, StarIcon} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {FeedbackInsightFilters, InsightFilters} from "types/OperatorInsights";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import FeedbackChart from "../../../components/Chart/FeedbackChart";
import {FeedbackSubjectType} from "../../../types/FeedbackSubjectType";
import SiteSelect from "../../../components/InsightControls/SiteSelect";
import {Site} from "../../../types/Site";
import ExportButton from "../../../components/ExportButton";
import {useOperator} from "../../../context/OperatorContext";
import exhibits from "../../../services/ngage/exhibits";

// Helpers ----

const breadcrumbs = {"Performance": "/", "Feedback": "/insights/feedback"};

function toPercentage(rate?: number): string {
  return (100 * Number(rate)).toFixed(1) + '%';
}

// Component ----

function FeedbackInsights() {
  const {termFor} = useOperator();
  const [filters, setFilters] = useState<FeedbackInsightFilters>({...defaultFilters, role: null});
  const [site, setSite] = useState<Site | null>(null);

  const sinceInTimezone = useMemo(() => filters.since?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.since]);
  const untilInTimezone = useMemo(() => filters.until?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.until]);

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'feedback', {...filters, since: sinceInTimezone, until: untilInTimezone}],
    ({signal}) => ngage.insights.feedback({...filters, since: sinceInTimezone, until: untilInTimezone}, {signal}),
    {
      keepPreviousData: true,
      enabled: !!sinceInTimezone && !!untilInTimezone,
    }
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectSite(site: Site | null) {
    setSite(site);
    setFilters({...filters, site: site?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.feedbackExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-feedback-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page
      title="Feedback"
      subTitle={`Understand more about how app users are leaving feedback on ${termFor('Exhibits')}, ${termFor('Events')} and ${termFor('Trails')}`}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <SiteSelect id="select-site" name="site" label={termFor('Site')} onChange={selectSite}/>
        </InsightControls>
      </Card>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={3}>
          <StatBox
            name="Feedback Count"
            icon={InboxIcon}
            stat={data?.summary.feedback_count.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times that your audience left feedback, between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Positive Feedback"
            icon={ThumbUpIcon}
            stat={toPercentage(data?.summary?.positive_feedback_rate)}
            isLoading={isLoading}
            tooltip={`The proportion of audience feedback that was positive, between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Negative Feedback"
            icon={ThumbDownIcon}
            stat={toPercentage(data?.summary?.negative_feedback_rate)}
            isLoading={isLoading}
            tooltip={`The proportion of audience feedback that was negative, between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Comment Rate"
            icon={ChatAltIcon}
            stat={toPercentage(data?.summary?.comment_rate)}
            isLoading={isLoading}
            tooltip={`The proportion of eligible audience feedback that included comments, between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Avg. CSAT Score"
            icon={StarIcon}
            stat={data?.summary?.mean_csat_score?.toFixed(1) ?? 0}
            isLoading={isLoading}
            tooltip={`The mean average customer satisfaction score for audience feedback, between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Avg. Comment Length"
            icon={PencilIcon}
            stat={Math.round(Number(data?.summary?.mean_comment_length)).toLocaleString() + ' chars'}
            isLoading={isLoading}
            tooltip={`The mean average length of comments left by your audience, between ${sinceDateString} and ${untilDateString}.`}
          />
        </StatList>
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">

        <Card
          heading="Events"
          subheading={`Event feedback per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <FeedbackChart data={data} interval={filters.interval} compare={filters.compare} subjectType={FeedbackSubjectType.EVENT} />}
        </Card>

        <Card
          heading="Exhibits"
          subheading={`Exhibit feedback per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <FeedbackChart data={data} interval={filters.interval} compare={filters.compare} subjectType={FeedbackSubjectType.EXHIBIT} />}
        </Card>

        <Card
          heading="Trails"
          subheading={`Trail feedback per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <FeedbackChart data={data} interval={filters.interval} compare={filters.compare} subjectType={FeedbackSubjectType.TRAIL} />}
        </Card>

      </div>
    </Page>
  );
}

// Export ----

export default FeedbackInsights;